/**
 * Shared product-type registry — 2026-07.
 *
 * The dictionary lives on the backend (`GET /api/product-types`) and is
 * managed from the Settings page. This module fetches it once at app boot
 * (see AuthContext) and caches it on ``window.__quotesys_product_types__``,
 * plus notifies subscribers via a tiny event bus so ProductFilters /
 * ProductForm / ProductsGrid update the moment an admin adds or renames a
 * type in another tab.
 *
 * Callers use ``useProductTypes()`` for reactive access, or
 * ``getProductTypeName(code, lang)`` for one-off label resolution (useful
 * inside cell formatters that would otherwise re-render on every list row).
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";

const CACHE_KEY = "__quotesys_product_types__";
const listeners = new Set();

function _emit() {
  for (const cb of listeners) {
    try { cb(getCachedTypes()); } catch { /* subscriber threw — ignore */ }
  }
}

export function getCachedTypes() {
  if (typeof window === "undefined") return [];
  return window[CACHE_KEY] || [];
}

export async function refreshProductTypes() {
  try {
    const { data } = await api.get("/product-types", { params: { include_disabled: true } });
    if (typeof window !== "undefined") {
      window[CACHE_KEY] = data;
    }
    _emit();
    return data;
  } catch {
    return getCachedTypes();
  }
}

/** React hook — returns the current type list and re-renders on refresh. */
export function useProductTypes({ onlyEnabled = true } = {}) {
  const [types, setTypes] = useState(getCachedTypes());
  useEffect(() => {
    if (!getCachedTypes().length) refreshProductTypes();
    const cb = (t) => setTypes(t);
    listeners.add(cb);
    return () => { listeners.delete(cb); };
  }, []);
  return onlyEnabled ? types.filter((t) => t.enabled) : types;
}

/** One-off label lookup — falls back to raw code when the type is unknown. */
export function getProductTypeName(code, lang = "zh") {
  if (!code) return "";
  const found = getCachedTypes().find((t) => t.code === code);
  if (!found) return code;  // preserves legacy value ("已删除类型" shown by callers)
  return (lang === "en" ? found.name_en : found.name_zh) || found.code;
}
